import { useEffect, useRef, useState } from 'react';

import { observeElementResize } from '#/lib/observe-element-resize';
import { COLORS } from '#/lib/utils';

interface AnimatedPathProps {
  borderRadius?: number;
  strokeWidth?: number;
  topMotionId: string;
  bottomMotionId: string;
  strokeColor?: string;
}

export const AnimatedBorderGenerator = ({
  borderRadius = 20,
  topMotionId,
  bottomMotionId,
  strokeWidth = 2,
  strokeColor = COLORS.grayscale['04'],
}: AnimatedPathProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 100, height: 100 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let lastWidth = 0;
    let lastHeight = 0;
    const updateDimensions = (width: number, height: number) => {
      if (
        Math.abs(width - lastWidth) < 1 &&
        Math.abs(height - lastHeight) < 1
      ) {
        return;
      }
      lastWidth = width;
      lastHeight = height;
      setDimensions({ width, height });
    };

    updateDimensions(el.clientWidth, el.clientHeight);
    return observeElementResize(el, ([entry]) => {
      if (!entry) return;
      updateDimensions(entry.contentRect.width, entry.contentRect.height);
    });
  }, []);

  const { width, height } = dimensions;

  return (
    <div
      ref={containerRef}
      className="absolute -top-5 -left-5 -right-5 -bottom-5 w-[calc(100%+10px)] h-[calc(100%+10px)]"
    >
      <svg
        className="block"
        width={width + 5}
        height={height + 5}
        viewBox={`0 0 ${width + 5} ${height + 5}`}
      >
        <rect
          x="5"
          y="5"
          width={width - 10}
          height={height - 10}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          rx={borderRadius}
          ry={borderRadius}
          shapeRendering="geometricPrecision"
        />
        <rect
          id={bottomMotionId}
          x="5"
          y="5"
          width={width - 10}
          height={height - 10}
          fill="none"
          className="stroke-electric-04 svg-draw"
          strokeWidth={strokeWidth * 1.5}
          rx={borderRadius}
          ry={borderRadius}
          shapeRendering="geometricPrecision"
        />
        <rect
          id={topMotionId}
          x="5"
          y="5"
          width={width - 10}
          height={height - 10}
          fill="none"
          className="stroke-electric-04 svg-draw rotate-180 -translate-x-5 -translate-y-5 origin-center"
          strokeWidth={strokeWidth * 1.5}
          rx={borderRadius}
          ry={borderRadius}
          shapeRendering="geometricPrecision"
        />
      </svg>
    </div>
  );
};
